import { useCallback } from 'react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  RegistryOrderCloseRequest,
  RegistryOrderDetail,
  RegistryOrderQueuePage,
  RegistryOrderQueueQuery,
  RegistryOrderQuoteRecordRequest,
  RegistryOrderReplyRequest,
} from '@/types/registry-documents';
import { useRegistryPage } from './useRegistryPage';

/** Orders move when one is sent and when a delivery lands: minutes apart at
 * the fastest, and nobody watches this list for a single order. */
export const REGISTRY_ORDER_QUEUE_POLL_MS = 60_000;

const EMPTY: RegistryOrderQueuePage = { items: [], total: 0, version: '' };

/**
 * A page of every team's registry-document orders, polled with ETag
 * revalidation (`useRegistryPage`).
 *
 * `close` ends an order from the queue and re-reads the page, on failure
 * too: a refused close means the order moved underneath the screen.
 */
export function useRegistryOrderQueue(query: RegistryOrderQueueQuery) {
  const { page, isLoading, error, refetch } = useRegistryPage(
    query,
    (sent, options) => vnextApiClient.listRegistryOrderQueue(sent, options),
    REGISTRY_ORDER_QUEUE_POLL_MS,
    EMPTY
  );

  const close = useCallback(
    async (orderId: string, request: RegistryOrderCloseRequest): Promise<RegistryOrderDetail> => {
      try {
        return await vnextApiClient.closeRegistryOrder(orderId, request);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  /** Answer a message from the research desk, then re-read the page: the
   * Reply badge clears, and a recorded quote gives an expected date. */
  const reply = useCallback(
    async (orderId: string, request: RegistryOrderReplyRequest): Promise<RegistryOrderDetail> => {
      try {
        return await vnextApiClient.replyToRegistryOrder(orderId, request);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  /** Record the desk's quote on its own, then re-read the page: the row
   * gains its expected date, and the Reply badge clears when the quote
   * names the desk's message. On failure too: a refusal means the order
   * moved underneath the screen. */
  const recordQuote = useCallback(
    async (
      orderId: string,
      request: RegistryOrderQuoteRecordRequest
    ): Promise<RegistryOrderDetail> => {
      try {
        return await vnextApiClient.recordRegistryQuote(orderId, request);
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  return { page, isLoading, error, refetch, close, reply, recordQuote };
}
